import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { onApiCall } from 'src/app/common/store/generic.reducer';
import { GenericState } from 'src/app/common/store/types';
import { SearchResult } from '../../types';
import { searchActions } from './search.actions';
import {
  Domain,
  Filter,
  OperationModel,
  SelectedRows,
  TemporaryActiveFilters,
} from './types';
import { getRandomInt } from '../../helper/random.helper';

export interface ISearchState {
  search: GenericState<SearchResult<any>>;
  operations: GenericState<OperationModel[]>;
  activeFilters: Filter[];
  customFilters: Filter[];
  contextFilters: Filter[];
  tempPermanentFilters: Filter[];
  temporaryActiveFilters: TemporaryActiveFilters;
  isContextFilterFlag: boolean;
  selectedRows: SelectedRows;
}

export const searchInitialState: ISearchState = {
  search: new GenericState<SearchResult<any>>(),
  operations: new GenericState<OperationModel[]>(),
  activeFilters: [],
  customFilters: [],
  contextFilters: [],
  tempPermanentFilters: [],
  temporaryActiveFilters: {
    tempActiveFilters: [],
    tempActiveFiltersUrlSource: '',
    tempActiveFiltersUrlTarget: '',
    tempActiveFiltersHopNumber: 0,
  },
  isContextFilterFlag: false,
  selectedRows: { data: [], isTotalRowCountSelected: false },
};
const searchReducers = [
  ...onApiCall<SearchResult<any>>(searchActions.search, 'search'),
  ...onApiCall<OperationModel[]>(searchActions.operations, 'operations'),
  on(
    searchActions.setActiveFilters,
    (state: ISearchState, { activeFilters }) => ({
      ...state,
      activeFilters: activeFilters,
    }),
  ),
  on(
    searchActions.setCustomFilters,
    (state: ISearchState, { customFilters }) => ({
      ...state,
      customFilters: customFilters,
    }),
  ),
  on(
    searchActions.setContextFilters,
    (state: ISearchState, { contextFilters, isContextFilterFlag }) => ({
      ...state,
      contextFilters: contextFilters,
      isContextFilterFlag: isContextFilterFlag,
    }),
  ),
  on(searchActions.setContextFilterFlag, (state: ISearchState, { flag }) => ({
    ...state,
    isContextFilterFlag: flag,
  })),
  on(
    searchActions.removeActiveFilterByIndex,
    (state: ISearchState, { index }) => {
      let activeFilter = [...state.activeFilters];
      activeFilter.splice(index, 1);

      state = {
        ...state,
        activeFilters: activeFilter,
      };
      return state;
    },
  ),
  on(searchActions.addTextFilter, (state: ISearchState, { domain }) => {
    let activeFilter = [...state.activeFilters];

    let textFilterExists = false;
    for (let filter of activeFilter) {
      if (filter.tag == 'Text' && filter.domains[0].field == domain.field) {
        //if there's already a filter with the same field, add to the same domain
        let domainToAdd: Domain = {
          field: domain.field,
          fieldLabel: domain.fieldLabel,
          operator: domain.operator,
          connector: domain.connector,
          value: domain.value,
          value2: domain.value2,
          text: domain.text,
          text2: domain.text2,
          isActive: domain.isActive,
        };
        filter.domains.push(domainToAdd);
        textFilterExists = true;
        break;
      }
    }
    //if there's isn't any text filter of the same type create a new one
    if (!textFilterExists) {
      activeFilter.push({
        name: domain.fieldLabel,
        tag: 'Text',
        domains: [
          {
            field: domain.field,
            fieldLabel: domain.fieldLabel,
            operator: domain.operator,
            connector: domain.connector,
            value: domain.value,
            value2: domain.value2,
            text: domain.text,
            text2: domain.text2,
            isActive: domain.isActive,
          },
        ],
        text: '',
        description: '',
        isReadOnly: false,
      });
    }

    state = {
      ...state,
      activeFilters: activeFilter,
    };
    return state;
  }),
  on(
    searchActions.toggleBuiltInFilter,
    (state: ISearchState, { builtInFilter: builtInFilter }) => {
      let activeFilters = [...state.activeFilters];
      //check if the chosen filter exists in the active filters
      let builtInFilterindex = activeFilters.findIndex(
        (activeFilter: Filter) => activeFilter.name == builtInFilter.name,
      );
      if (builtInFilterindex === -1) {
        //if the chosen filter doesn't exists, set it to active and add it to the active filter
        let chosenFilter = JSON.parse(JSON.stringify(builtInFilter));
        chosenFilter.domains.forEach((domain: Domain) => {
          domain.isActive = true;
        });
        activeFilters.push(chosenFilter);
      } else {
        //if the chosen filter exists in the active filters, remove it
        activeFilters.splice(builtInFilterindex, 1);
      }
      state = {
        ...state,
        activeFilters: activeFilters,
      };
      return state;
    },
  ),
  on(
    searchActions.addActiveFilter,
    (state: ISearchState, { filter, isContextFilterFlag }) => {
      let activeFilters = [...state.activeFilters];
      activeFilters.push(filter);
      state = {
        ...state,
        activeFilters: activeFilters,
        isContextFilterFlag: isContextFilterFlag,
      };
      return state;
    },
  ),
  on(
    searchActions.changeDomainState,
    (state: ISearchState, { filterIndex, domainIndex }) => {
      let activeFilters = JSON.parse(JSON.stringify(state.activeFilters));
      let customFilters = JSON.parse(JSON.stringify(state.customFilters));
      let customFilter: Filter = customFilters[filterIndex];

      let activeFilterIndex = activeFilters.findIndex(
        (x) => x.name == customFilter.name,
      );
      if (activeFilterIndex == -1) {
        customFilter.domains[domainIndex].isActive =
          !customFilter.domains[domainIndex].isActive;
        activeFilters.push(customFilter);
      } else {
        activeFilters[activeFilterIndex].domains[domainIndex].isActive =
          !activeFilters[activeFilterIndex].domains[domainIndex].isActive;

        let result = false;
        activeFilters[activeFilterIndex].domains.forEach((domain) => {
          if (domain.isActive) {
            result = true;
          }
        });
        if (!result) {
          activeFilters.splice(activeFilterIndex, 1);
        }
      }
      state = {
        ...state,
        activeFilters: activeFilters,
      };
      return state;
    },
  ),
  on(searchActions.applyCustomFilter, (state: ISearchState, { domains }) => {
    let activeFilters = [...state.activeFilters];
    let customFilters = [...state.customFilters];
    let name = '';
    domains.forEach((x) => {
      name = name + x.field + x.value + getRandomInt(1, 15000);
    });
    //create the filter from the domain array
    let customFilter: Filter = {
      name: name,
      tag: 'Custom',
      domains: domains,
      text: '',
      description: '',
      isReadOnly: false,
    };
    customFilters.push(customFilter);
    let custom: Filter = JSON.parse(JSON.stringify(customFilter));
    custom.domains.forEach((x) => (x.isActive = true));
    activeFilters.push(custom);
    state = {
      ...state,
      activeFilters: activeFilters,
      customFilters: customFilters,
    };
    return state;
  }),
  on(searchActions.resetActiveFilter, (state: ISearchState) => {
    const isContextFilterFlag = state.isContextFilterFlag;
    let activeFilter = [...state.activeFilters];
    if (isContextFilterFlag) {
      activeFilter = activeFilter.filter((x) => {
        return x.tag == 'SmartAction';
      });
      state = {
        ...state,
        activeFilters: activeFilter,
        isContextFilterFlag: false,
      };
    } else {
      state = {
        ...state,
        activeFilters: [],
      };
    }

    return state;
  }),
  on(
    searchActions.addDefaultActiveFilter,
    (state: ISearchState, { filter, isContextFilterFlag }) => {
      let activeFilters = [...state.activeFilters];
      if (!activeFilters.some((x) => x.name == filter.name)) {
        activeFilters.push(filter);
      }
      state = {
        ...state,
        activeFilters: activeFilters,
        isContextFilterFlag: isContextFilterFlag,
      };
      return state;
    },
  ),
  on(
    searchActions.setSelectedRows,
    (state: ISearchState, { selectedRows }) => ({
      ...state,
      selectedRows: {
        data: selectedRows.data,
        isTotalRowCountSelected: selectedRows.isTotalRowCountSelected,
      },
    }),
  ),
  on(searchActions.temporaryActivatePermanentFilters, (state: ISearchState) => {
    let activeFilters: Filter[] = state.activeFilters;
    let tempPermanentFilters: Filter[] = state.tempPermanentFilters;

    if (activeFilters.length === 0 && state.tempPermanentFilters.length > 0) {
      activeFilters = [...activeFilters, ...state.tempPermanentFilters];
      tempPermanentFilters = [];
    }

    return {
      ...state,
      activeFilters: activeFilters,
      tempPermanentFilters: tempPermanentFilters,
      isContextFilterFlag: false,
    };
  }),

  on(
    searchActions.temporaryDeactivatePermanentFilters,
    (state: ISearchState) => {
      const hasSmartActionFilter = state.activeFilters.some(
        (filter) => filter.tag === 'SmartAction',
      );

      let tempPermanentFilters: Filter[] = [];
      let activeFilters: Filter[] = state.activeFilters;
      if (hasSmartActionFilter) {
        activeFilters = state.activeFilters.filter(
          (filter) => filter.tag !== 'Permanent',
        );
        tempPermanentFilters = state.activeFilters.filter(
          (filter) => filter.tag === 'Permanent',
        );
      }

      return {
        ...state,
        activeFilters: activeFilters,
        tempPermanentFilters: tempPermanentFilters,
        isContextFilterFlag: false,
      };
    },
  ),

  on(
    searchActions.deactivatePropertyFiltersIfSmartActionsEnabled,
    (state: ISearchState) => {
      const hasSmartActionFilter = state.activeFilters.some(
        (filter) => filter.tag === 'SmartAction',
      );

      let activeFilters: Filter[] = state.activeFilters;
      if (hasSmartActionFilter) {
        activeFilters = state.activeFilters.filter(
          (filter) => filter.tag !== 'Property',
        );
      }

      return {
        ...state,
        activeFilters: activeFilters,
        isContextFilterFlag: false,
      };
    },
  ),
  on(
    searchActions.togglePropertyFilter,
    (state: ISearchState, { propertyFilter, filterItem }) => {
      let activeFilters = [...state.activeFilters];

      if (propertyFilter.isReadOnly) return state;

      let existingItemIndex = activeFilters.findIndex(
        (activeFilter: Filter) =>
          activeFilter.tag === 'Property' &&
          activeFilter.name ===
            propertyFilter.propertyId + '/' + filterItem.label,
      );

      if (existingItemIndex === -1) {
        if (!propertyFilter.allowMultiple) {
          activeFilters = activeFilters.filter(
            (filter) =>
              filter.tag !== 'Property' ||
              !filter.name.startsWith(propertyFilter.propertyId),
          );
        }

        //if the chosen filter doesn't exists, set it to active and add it to the active filter
        let chosenFilter = JSON.parse(JSON.stringify(filterItem));
        chosenFilter.domains.forEach((domain: Domain) => {
          domain.isActive = true;
        });

        const filter: Filter = {
          description: '',
          domains: chosenFilter.domains,
          isReadOnly: false,
          name: propertyFilter.propertyId + '/' + chosenFilter.label,
          tag: propertyFilter.tag,
          text: chosenFilter.label,
        };

        activeFilters.push(filter);
      } else {
        //if the chosen filter exists in the active filters, remove it
        activeFilters.splice(existingItemIndex, 1);
      }

      state = {
        ...state,
        activeFilters: activeFilters,
      };
      return state;
    },
  ),
  on(
    searchActions.temporaryKeepActiveFilters,
    (state: ISearchState, { urlSource, urlTarget }) => {
      const temporaryActivesFilters: TemporaryActiveFilters = {
        tempActiveFilters: state.activeFilters,
        tempActiveFiltersUrlSource: urlSource,
        tempActiveFiltersUrlTarget: urlTarget,
        tempActiveFiltersHopNumber: 0,
      };

      return {
        ...state,
        temporaryActiveFilters: temporaryActivesFilters,
      };
    },
  ),
  on(
    searchActions.temporaryActivateTempActiveFilters,
    (state: ISearchState, { urlSource }) => {
      const temporaryActivesFiltersState: TemporaryActiveFilters =
        state.temporaryActiveFilters;
      const activeFilters: Filter[] = state.activeFilters;

      const isValidHop: boolean =
        temporaryActivesFiltersState.tempActiveFiltersHopNumber < 1;
      const hasTempFilters: boolean =
        temporaryActivesFiltersState.tempActiveFilters.length > 0;
      const isUrlCanActivate: boolean =
        temporaryActivesFiltersState.tempActiveFiltersUrlSource === urlSource;

      const isValidToActivateTemp: boolean =
        hasTempFilters && isValidHop && isUrlCanActivate;

      const temporaryActivesFilters: TemporaryActiveFilters = {
        tempActiveFilters: [],
        tempActiveFiltersUrlSource: '',
        tempActiveFiltersUrlTarget: '',
        tempActiveFiltersHopNumber: 0,
      };

      return {
        ...state,
        activeFilters: !isValidToActivateTemp
          ? activeFilters
          : temporaryActivesFiltersState.tempActiveFilters,
        temporaryActiveFilters: temporaryActivesFilters,
      };
    },
  ),
  on(searchActions.updateHopNumber, (state: ISearchState, { urlTarget }) => {
    let temporaryActivesFiltersState: TemporaryActiveFilters =
      state.temporaryActiveFilters;

    const isUrlTarget: boolean =
      temporaryActivesFiltersState.tempActiveFiltersUrlTarget === urlTarget;

    const temporaryActivesFilters: TemporaryActiveFilters = {
      tempActiveFilters: temporaryActivesFiltersState.tempActiveFilters,
      tempActiveFiltersUrlSource:
        temporaryActivesFiltersState.tempActiveFiltersUrlSource,
      tempActiveFiltersUrlTarget:
        temporaryActivesFiltersState.tempActiveFiltersUrlTarget,
      tempActiveFiltersHopNumber: isUrlTarget
        ? temporaryActivesFiltersState.tempActiveFiltersHopNumber
        : temporaryActivesFiltersState.tempActiveFiltersHopNumber + 1,
    };

    return {
      ...state,
      temporaryActiveFilters: temporaryActivesFilters,
    };
  }),
];

export const searchReducer = createReducer(
  searchInitialState,
  ...searchReducers,
) as ActionReducer<ISearchState, Action>;

export function useReducerFunc(state: any | undefined, action: Action) {
  return searchReducer(state, action);
}
